"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const _ActionTypes = require("./actionTypes");
const _Actions = require("./actions");
const sok_types_1 = require("@caddigital/sok-types");
const toast_1 = require("../../../actions/toast");
const CartActions = require("../../../actions/cart");
const errorHandling_1 = require("../../../utils/errorHandling");
const validation_1 = require("../../../utils/validation");
const validationBits = sok_types_1.CustomMeta.FILES_LIST | sok_types_1.CustomMeta.REQUIRED | sok_types_1.CustomMeta.EDITABLE;
function default_1(namespace) {
    const ActionTypes = _ActionTypes.default(namespace);
    const Actions = _Actions.default(namespace);
    const validateNewFolder = (action) => {
        const safe = (0, validation_1.ap)((0, validation_1.hasSafeText)('name')(action.payload.name));
        const validation = safe((0, validation_1.isNotEmpty)('name')({ name: action.payload.name }));
        return validation.fold((e) => (0, rxjs_1.throwError)(Actions.validationFailure(e)), () => (0, rxjs_1.of)(action));
    };
    function createFolder(action$, store, { ajax, getToken, baseUrl }) {
        return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.SAVE_NEW_FOLDER_REQUEST), (0, operators_1.mergeMap)((action) => validateNewFolder(action).pipe((0, operators_1.mergeMap)((action) => {
            const { payload } = action;
            return ajax({
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: getToken()
                },
                url: `${baseUrl}api/files/check-existing`,
                body: {
                    files: [
                        {
                            name: payload.name,
                            type: 'Folder'
                        }
                    ],
                    parent: payload.parent
                },
                timeout: 3600000,
                method: 'POST'
            }).pipe((0, operators_1.timeout)(360000), (0, operators_1.mergeMap)(() => (0, rxjs_1.of)(action)));
        }), (0, operators_1.mergeMap)(({ payload }) => ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: 'POST',
            url: `${baseUrl}api/files/`,
            body: {
                name: payload.name,
                type: payload.type,
                parent: payload.parent,
                description: payload.description
            }
        }).pipe((0, operators_1.mergeMap)(({ response }) => {
            const validation = sok_types_1.File._iFolderMinimal.decode(response);
            const act = validation.fold((e) => Actions.saveNewFolderFailure((0, errorHandling_1.parseValidationError)(e)), () => Actions.saveNewFolderSuccess());
            return (0, rxjs_1.of)(act);
        }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.saveNewFolderFailure((0, errorHandling_1.parseAjaxErrorResponse)(error)))))), (0, operators_1.catchError)((action) => (0, rxjs_1.of)(action)))));
    }
    function update(action$, store, { ajax, getToken, baseUrl }) {
        return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.SAVE_PROPERTIES_REQUEST), (0, operators_1.mergeMap)((action) => {
            const { payload } = action;
            const nameMeta = payload.metadata.find((x) => x.identifier === 'name-meta');
            const file = Object.assign({}, payload.file);
            if (nameMeta && nameMeta.id !== undefined) {
                const val = payload.values[nameMeta.id];
                if (val && typeof val === 'string') {
                    file.name = val;
                    if (file.type !== 'FOLDER' && file.fileExtension !== undefined) {
                        file.name += `.${file.fileExtension}`;
                    }
                }
            }
            return ajax({
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: getToken()
                },
                url: `${baseUrl}api/files/check-existing`,
                body: { files: [file], parent: file.parent },
                method: 'POST'
            }).pipe((0, operators_1.mergeMap)((res) => {
                const required = payload.file.type === 'FOLDER'
                    ? []
                    : payload.metadata.filter((x) => x.identifier === 'name-meta' ||
                        (x.bitField & validationBits) === validationBits);
                const validation = required.reduce((acc, x) => {
                    if (x.id === undefined) {
                        return acc;
                    }
                    return (0, validation_1.ap)((0, validation_1.isNotEmpty)(x.id.toString(), x.identifier)(payload.values))(acc);
                }, (0, validation_1.isUnique)('name-meta')(!res.response.data));
                if (validation.isFailure()) {
                    return (0, rxjs_1.of)(Actions.validationFailure(validation.value));
                }
                return ajax({
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: getToken()
                    },
                    method: 'PATCH',
                    body: { file: payload.file.id, values: action.payload.values },
                    url: action.payload.mode === 'EDIT'
                        ? `${baseUrl}api/files/${payload.file.id}`
                        : `${baseUrl}api/subproject/${payload.file.id}`
                }).pipe((0, operators_1.mergeMap)(() => (0, rxjs_1.merge)((0, rxjs_1.of)(Actions.savePropertiesSuccess(payload.file)))), (0, operators_1.catchError)((error) => (0, rxjs_1.merge)((0, rxjs_1.of)(Actions.savePropertiesFailure((0, errorHandling_1.parseAjaxErrorResponse)(error))))));
            }), (0, operators_1.catchError)((r) => (0, rxjs_1.of)(r)));
        }));
    }
    function getTTL(action$, _store, { ajax, getToken, baseUrl }) {
        return action$.ofType(ActionTypes.GET_TTL_REQUEST).pipe((0, operators_1.mergeMap)(({ folder }) => ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: 'GET',
            url: `${baseUrl}api/files/${folder.uuid}/scheduled-deletion`
        }).pipe((0, operators_1.mergeMap)(({ response }) => {
            const validation = sok_types_1.File.iScheduledDeletionConfig.decode(response);
            const act = validation.fold((e) => Actions.getTTLFailure((0, errorHandling_1.parseValidationError)(e)), (x) => Actions.getTTLSuccess(x));
            return (0, rxjs_1.of)(act);
        }), (0, operators_1.catchError)((e) => {
            const error = (0, errorHandling_1.parseAjaxErrorResponse)(e);
            return (error === null || error === void 0 ? void 0 : error.type) === 'NOT_FOUND'
                ? (0, rxjs_1.of)(Actions.getTTLSuccess(null))
                : (0, rxjs_1.of)(Actions.getTTLFailure(error));
        }))), (0, operators_1.catchError)((r) => (0, rxjs_1.of)(r)));
    }
    function setTTL(action$, _store, { ajax, getToken, baseUrl }) {
        return action$.ofType(ActionTypes.SET_TTL_REQUEST).pipe((0, operators_1.mergeMap)(({ folderUuid, ttl }) => ajax({
            method: ttl ? 'POST' : 'DELETE',
            url: `${baseUrl}api/files/${folderUuid}/scheduled-deletion`,
            body: ttl !== null && ttl !== void 0 ? ttl : {},
            headers: {
                Authorization: getToken(),
                'Content-Type': 'application/json'
            }
        }).pipe((0, operators_1.mergeMap)(({ response, status }) => {
            if (status === 204)
                return (0, rxjs_1.of)(Actions.setTTLSuccess(null), Actions.closeOverlay());
            const validation = sok_types_1.File.iScheduledDeletionConfig.decode(response);
            const act = validation.fold((e) => [Actions.setTTLFailure((0, errorHandling_1.parseValidationError)(e))], (x) => [Actions.setTTLSuccess(x), Actions.closeOverlay()]);
            return (0, rxjs_1.of)(...act);
        }), (0, operators_1.catchError)((error) => (0, rxjs_1.merge)((0, rxjs_1.of)(Actions.setTTLFailure((0, errorHandling_1.parseAjaxErrorResponse)(error))))))), (0, operators_1.catchError)((r) => (0, rxjs_1.of)(r)));
    }
    function deleteFile(action$, store, { ajax, getToken, baseUrl }) {
        return action$.ofType(ActionTypes.DELETE_REQUEST).pipe((0, operators_1.mergeMap)(({ files }) => ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: 'DELETE',
            url: `${baseUrl}api/files/`,
            body: { id: files.map((f) => f.id) }
        }).pipe((0, operators_1.mergeMap)((_) => {
            const state = store.value;
            const { currentFolder } = state.files;
            const { currentProject } = state.project;
            const ids = files.map((f) => f.id);
            const cartItems = state.cart.data.items;
            // can be revision or folder id
            const removeIds = [];
            for (const i in cartItems) {
                const item = cartItems[i];
                if (item.type !== 'FOLDER') {
                    if (ids.includes(item.file)) {
                        removeIds.push(item.id);
                    }
                }
                else {
                    if (ids.includes(item.id)) {
                        removeIds.push(item.id);
                    }
                }
            }
            const actions = [Actions.deleteSuccess(files)];
            const currentProjectId = currentProject.status === 'FETCHED'
                ? currentProject.project.id
                : undefined;
            const currentFolderId = currentFolder !== null ? currentFolder.id : undefined;
            if (currentFolderId !== undefined &&
                currentProjectId !== undefined) {
                if (removeIds.length > 0) {
                    actions.push(CartActions.removeItems(removeIds, currentProjectId));
                }
            }
            return (0, rxjs_1.of)(...actions);
        }), (0, operators_1.catchError)((error) => {
            const err = (0, errorHandling_1.parseAjaxErrorResponse)(error);
            const actions = [Actions.deleteFailure(err)];
            err &&
                err.type === 'CONFLICT' &&
                actions.push((0, toast_1.addNewToast)('Delete', 'Permission denied', 'FAILURE', []));
            return (0, rxjs_1.of)(...actions);
        }))));
    }
    return { update, deleteFile, createFolder, getTTL, setTTL };
}
exports.default = default_1;
//# sourceMappingURL=epics.js.map